<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300">
      {{ $t('applicationTrackingMenu') }}
    </h1>

    <div class="w-full flex justify-center">
      <div class="rounded-md border border-brand-gray-300 p-2 w-full md:w-96">
        <Form
          v-if="!isLoading"
          class="flex flex-col gap-3"
          v-slot="{ resetForm }"
          @submit="(values) => onSubmit(values, resetForm)"
        >
          <Field
            :rules="validEmail"
            validate-on-input
            validate-on-change
            validate-on-blur
            name="email"
            v-slot="{ field, errors: errs }"
          >
            <TextInput
              :field="field"
              :errors="errs"
              :label="$t('applicantEmail')"
              label-class="w-full"
              class="w-full"
              direction="flex-col"
            />
          </Field>
          <Field
            :rules="
              (value) => {
                return requiredRule(value) && minChar(15, value)
              }
            "
            validate-on-input
            validate-on-change
            validate-on-blur
            name="requestNumber"
            v-slot="{ field, errors: errs }"
          >
            <TextInput
              :field="field"
              :errors="errs"
              :label="$t('referenceNumber')"
              mask="numberOnly"
              label-class="w-full"
              class="w-full"
              direction="flex-col"
            />
          </Field>
          <fieldset class="border-2 border-primary rounded p-4 flex flex-col gap-y-5">
            <legend class="px-2">{{ $t('verifyApplicant') }}</legend>
            <Field :rules="requiredRule" name="recaptchaResponse" v-slot="{ handleChange }">
              <GRecaptcha
                :key="rando"
                ref="gRecaptchaRef"
                class="flex justify-center"
                @successCallback="(args) => handleChange('recaptchaResponse', args)"
              ></GRecaptcha>
            </Field>
          </fieldset>

          <div class="w-full flex justify-end">
            <button
              class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
            >
              <div class="p-2 bg-primary rounded-s-md">
                <faMagnifyingGlass class="w-4 fill-white"></faMagnifyingGlass>
              </div>
              <span class="w-full text-center">
                {{ $t('Search') }}
              </span>
              <div class="p-0.5"></div>
            </button>
          </div>
        </Form>
      </div>
    </div>

    <ResultView v-if="!isFetching" :request="request" />
  </div>
</template>

<script setup>
import faMagnifyingGlass from '@/assets/icons/faMagnifyingGlass.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import client_config from '@/assets/json/client_config.json'
import GRecaptcha from '@/components/GRecaptcha/index.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import { isValidEmail } from '@/composables/useRuleHandler.js'
import axiosInstance from '@/services'
import { useAuthStore } from '@/stores/auth'
import ResultView from '@/views/services/public-visa/application-tracking/ResultView.vue'
import { Field, Form } from 'vee-validate'
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()
const authStore = useAuthStore()
const isLoading = ref(true)
const isFetching = ref(true)
const rando = ref()
const initialValues = ref()

const {
  subscriptionTransactions: { typingCenterForIcaSubscription },
  module: { ICAandResi, ICA },
  application: { guest },
  eidaServices: { replaceIDCardForCitizen },
  requestActions: { approved },
  ServiceTransactions: { ResidencePermitsForStayingOutsideUaeOverSixMonths, icaRefund }
} = client_lookups

const { enableUpdateDeliveryInformationFromGuest } = client_config

const request = reactive({
  entity: {},
  history: {},
  showHeaderResult: false,
  isDraft: null,
  isRequest: false,
  typingCenterRegRequest: [],
  returnDataSearch: false,
  isIcaTypingCenterRegistration: false,
  requestLastHis: {},
  isApprove: false
})

function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

function validEmail(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return isValidEmail(value) || 'INVALID_EMAIL'
}

function minChar(char, value) {
  if (!value) return i18n.t('INVALID_REQUIRED')

  if (value?.trim().length < char) {
    return i18n.t('INVALID_MIN_CHAR', [char])
  }

  return true
}

onMounted(async () => {
  rando.value = Math.random()

  if (!authStore.isLoggedIn) {
    await authStore.guestLogin()
  }

  isLoading.value = false
})

const payload = reactive({
  requestType: 1,
  isUsingCaptcha: true,
  pageSize: 10,
  pageNumber: 1,
  currentPage: 1,
  ServicesTransactions: []
})

async function getDraftRequest(form) {
  const body = {
    ...payload,
    ...form
  }
  const response = await axiosInstance.post('/api/guest/visa/draft/searchForDrafts', body)

  const { searchRequestDraft } = response

  if ((searchRequestDraft?.collection ?? []).length > 0) {
    request.entity = searchRequestDraft.collection[0]

    if (request.entity.requestNumber) {
      request.showHeaderResult = true
      request.isDraft = true
      request.isRequest = false
      request.history = searchRequestDraft.collection[0]
    }
  }
}

/**
 * this is a refactoring of getCurrentRequest
 * web/client/guest/pages/applicationTracking/applicationTrackingCtrl.js
 */
async function searchTrackingRequest(form) {
  const body = {
    ...payload,
    ...form
  }

  const response = await axiosInstance.post('/api/guest/visa/request/searchRequestsTracking', body)

  // clear the recaptcha
  rando.value = Math.random()

  const {
    requestMasters: { collection },
    requestsForNonGuests,
    requestActions
  } = response

  if (collection && collection.length > 0) {
    if ([476, 478, 545, 479, 714].includes(requestsForNonGuests.serviceTransId)) return

    request.typingCenterRegRequest = requestsForNonGuests
    request.entity = requestsForNonGuests

    if (requestsForNonGuests.requestNumber && request.returnDataSearch) {
      // TODO
    } else {
      request.showHeaderResult = false
      request.showNoData = true
    }

    if (requestsForNonGuests.serviceTransId == 162) {
      /**
       * TODO: process this logic
       * web/client/guest/pages/applicationTracking/applicationTrackingCtrl.js
       * getCurrentRequest
       * apiHelperService.get("user/checkIfNotFreeZone", options);
       */
    }

    if (requestsForNonGuests.serviceTransId == typingCenterForIcaSubscription) {
      request.isIcaTypingCenterRegistration = true
    }

    request.entity = collection[0]

    if (collection[0]?.requestNumber) {
      request.isDraft = false
      request.isRequest = true
      request.showHeaderResult = true
      request.returnDataSearch = true
      request.requestLastHis = collection[0]

      const { serviceTransId } = collection[0]

      if (collection[0].module) {
        const { id: moduleId, applicationId } = collection[0].module
        const isICA =
          [ICAandResi, ICA].includes(moduleId) && enableUpdateDeliveryInformationFromGuest
        const isGuest = applicationId == guest
        const isReplaceIDCardForCitizen = serviceTransId == replaceIDCardForCitizen
        if (isICA) {
          request.showUpdateDeliveryAction = isGuest && isReplaceIDCardForCitizen
        }
      }

      request.history = requestActions
      if (requestActions?.collection) {
        request.history = requestActions.collection[0]
      }

      request.isApprove = request.history?.find((x) => x.actionId == approved)
      if (
        request.isApprove &&
        serviceTransId == ResidencePermitsForStayingOutsideUaeOverSixMonths
      ) {
        /**
         *  TODO: process this logic
         *  web/client/guest/pages/applicationTracking/applicationTrackingCtrl.js
         *  setOverstayedPermitLastEntryDate(approvedAction);
         */
      }
    } else {
      /**
       * TODO: process this logic
       * web/client/guest/pages/applicationTracking/applicationTrackingCtrl.js
       * getCurrentRequest
       */
    }
  }

  isFetching.value = false
}

const onSubmit = async (form) => {
  isFetching.value = true
  const { requestNumber } = form

  if (requestNumber.startsWith('00')) {
    return await getDraftRequest(form)
  }

  return await searchTrackingRequest(form)
}
</script>

<style lang="scss" scoped></style>
